import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Navigate, Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { stockApi } from '../services/api';
import axios from 'axios';
import { MagnifyingGlass } from '@phosphor-icons/react';
import { formatINR, formatMarketCap, formatVolume, formatPercent } from '../utils/formatters';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Screener() {
  const { user, loading } = useAuth();
  const [stocks, setStocks] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [filters, setFilters] = useState({ min_price: '', max_price: '', sector: '' });
  const [searchTerm, setSearchTerm] = useState('');

  const loadStocks = useCallback(async () => {
    try {
      const params = {};
      if (filters.min_price) params.min_price = parseFloat(filters.min_price);
      if (filters.max_price) params.max_price = parseFloat(filters.max_price);
      if (filters.sector) params.sector = filters.sector;
      const { data } = await stockApi.screener(params);
      setStocks(data);
    } catch {
      setStocks([]);
    }
  }, [filters]);

  useEffect(() => {
    const loadSectors = async () => {
      try {
        const { data } = await axios.get(`${API}/stocks/sectors`, { withCredentials: true });
        setSectors(data);
      } catch {
        setSectors([]);
      }
    };
    loadSectors();
  }, []);

  useEffect(() => {
    if (user) {
      loadStocks();
    }
  }, [user, loadStocks]);

  if (loading) return <div className="min-h-screen flex items-center justify-center" style={{ background: '#0A0B0E' }}><div className="text-white">Loading...</div></div>;
  if (!user) return <Navigate to="/login" />;

  const filteredStocks = stocks.filter(s =>
    s.symbol.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Layout>
      <div className="w-full max-w-[1920px] mx-auto px-6 py-8" data-testid="screener-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-4xl font-heading font-black tracking-tighter text-white">Nifty 50 Screener</h1>
            <p className="text-sm text-[#94A3B8] mt-1">Filter and analyze NSE-listed stocks</p>
          </div>
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-[#94A3B8]">
            {filteredStocks.length} stocks
          </div>
        </div>

        {/* Search & Filters */}
        <div className="bg-[#12141A] border border-white/10 p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="md:col-span-2">
              <label className="block text-xs font-mono uppercase tracking-[0.2em] text-[#94A3B8] mb-2">Search</label>
              <div className="relative">
                <MagnifyingGlass size={20} className="absolute left-3 top-2.5 text-[#64748B]" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="RELIANCE, TCS, HDFC..."
                  className="w-full pl-10 pr-3 py-2 bg-[#0A0B0E] border border-white/10 text-white text-sm focus:outline-none focus:border-[#2563EB] transition-colors"
                  data-testid="screener-search-input"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-mono uppercase tracking-[0.2em] text-[#94A3B8] mb-2">Sector</label>
              <select
                value={filters.sector}
                onChange={(e) => setFilters({ ...filters, sector: e.target.value })}
                className="w-full px-3 py-2 bg-[#0A0B0E] border border-white/10 text-white text-sm focus:outline-none focus:border-[#2563EB] transition-colors"
                data-testid="screener-sector"
              >
                <option value="">All Sectors</option>
                {sectors.map((sec) => (
                  <option key={sec} value={sec}>{sec}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-mono uppercase tracking-[0.2em] text-[#94A3B8] mb-2">Min Price ₹</label>
              <input
                type="number"
                value={filters.min_price}
                onChange={(e) => setFilters({ ...filters, min_price: e.target.value })}
                className="w-full px-3 py-2 bg-[#0A0B0E] border border-white/10 text-white text-sm focus:outline-none focus:border-[#2563EB] transition-colors"
                placeholder="0"
                data-testid="screener-min-price"
              />
            </div>
            <div>
              <label className="block text-xs font-mono uppercase tracking-[0.2em] text-[#94A3B8] mb-2">Max Price ₹</label>
              <input
                type="number"
                value={filters.max_price}
                onChange={(e) => setFilters({ ...filters, max_price: e.target.value })}
                className="w-full px-3 py-2 bg-[#0A0B0E] border border-white/10 text-white text-sm focus:outline-none focus:border-[#2563EB] transition-colors"
                placeholder="15000"
                data-testid="screener-max-price"
              />
            </div>
          </div>
        </div>

        {/* Results */}
        <div className="bg-[#12141A] border border-white/10 overflow-hidden" data-testid="screener-results">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b border-white/10 bg-[#0A0B0E]/50">
                  <th className="text-left p-4 text-xs font-mono uppercase tracking-[0.2em] text-[#94A3B8]">Symbol</th>
                  <th className="text-left p-4 text-xs font-mono uppercase tracking-[0.2em] text-[#94A3B8]">Name</th>
                  <th className="text-left p-4 text-xs font-mono uppercase tracking-[0.2em] text-[#94A3B8]">Sector</th>
                  <th className="text-right p-4 text-xs font-mono uppercase tracking-[0.2em] text-[#94A3B8]">Price</th>
                  <th className="text-right p-4 text-xs font-mono uppercase tracking-[0.2em] text-[#94A3B8]">Change</th>
                  <th className="text-right p-4 text-xs font-mono uppercase tracking-[0.2em] text-[#94A3B8]">Volume</th>
                  <th className="text-right p-4 text-xs font-mono uppercase tracking-[0.2em] text-[#94A3B8]">Market Cap</th>
                </tr>
              </thead>
              <tbody>
                {filteredStocks.map((stock) => (
                  <tr
                    key={stock.symbol}
                    className="border-b border-white/5 hover:bg-white/[0.02] transition-colors"
                    data-testid={`screener-row-${stock.symbol}`}
                  >
                    <td className="p-4 font-mono font-semibold text-white">
                      <Link to={`/stock/${stock.symbol}`} className="hover:text-[#2563EB] transition-colors">{stock.symbol}</Link>
                    </td>
                    <td className="p-4 text-[#94A3B8] text-sm">{stock.name}</td>
                    <td className="p-4 text-[#64748B] text-xs font-mono uppercase">{stock.sector}</td>
                    <td className="p-4 text-right font-mono text-white">{formatINR(stock.price)}</td>
                    <td className={`p-4 text-right font-mono ${stock.change >= 0 ? 'text-[#00FF66]' : 'text-[#FF3366]'}`}>
                      {formatPercent(stock.change_percent)}
                    </td>
                    <td className="p-4 text-right font-mono text-[#94A3B8] text-sm">
                      {formatVolume(stock.volume)}
                    </td>
                    <td className="p-4 text-right font-mono text-[#94A3B8] text-sm">
                      {formatMarketCap(stock.market_cap)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Layout>
  );
}
